import { useState, type FormEvent } from 'react';
import { contactSubjects } from '@/mocks/data';

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    setError('');

    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      form.reset();
    }, 800);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-6">
          <i className="ri-check-line text-primary-600 text-2xl"></i>
        </div>
        <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-2">
          訊息已送出
        </h3>
        <p className="text-base text-foreground-500 max-w-sm leading-relaxed">
          感謝您的來訊，我們將盡快回覆。
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form="">
      {/* Honeypot */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-base font-medium text-foreground-800 mb-2">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-4 py-3 rounded-md bg-background-50 border border-foreground-200/60 text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/40 focus:ring-1 focus:ring-primary-400/20 transition-colors"
            placeholder="您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-base font-medium text-foreground-800 mb-2">
            Email <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-3 rounded-md bg-background-50 border border-foreground-200/60 text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/40 focus:ring-1 focus:ring-primary-400/20 transition-colors"
            placeholder="your@email.com"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-base font-medium text-foreground-800 mb-2">
          聯絡主題 <span className="text-primary-500">*</span>
        </label>
        <select
          id="contact-subject"
          name="subject"
          required
          className="w-full px-4 py-3 rounded-md bg-background-50 border border-foreground-200/60 text-base text-foreground-900 focus:outline-none focus:border-primary-400/40 focus:ring-1 focus:ring-primary-400/20 transition-colors appearance-none cursor-pointer"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666666' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'%3E%3C/path%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 1rem center',
            paddingRight: '2.5rem',
          }}
        >
          <option value="">請選擇主題</option>
          {contactSubjects.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-base font-medium text-foreground-800 mb-2">
          訊息 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          required
          maxLength={500}
          className="w-full px-4 py-3 rounded-md bg-background-50 border border-foreground-200/60 text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/40 focus:ring-1 focus:ring-primary-400/20 transition-colors resize-none"
          placeholder="請描述您的問題或需求（最多500字）"
        ></textarea>
        <p className="text-[13px] text-foreground-400 mt-1.5">最多 500 字</p>
      </div>

      <div className="flex items-start gap-3">
        <input
          id="contact-consent"
          name="consent"
          type="checkbox"
          required
          className="mt-0.5 w-4 h-4 rounded border-foreground-300 text-primary-500 focus:ring-primary-400/20 cursor-pointer"
        />
        <label htmlFor="contact-consent" className="text-[13px] text-foreground-500 leading-relaxed cursor-pointer">
          我同意樂律文化依據隱私權政策，收集、處理及利用我所提供的個人資料，以利回覆本次詢問。詳情請參閱隱私權政策。
        </label>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 bg-primary-50 border border-primary-200/50 rounded-md px-4 py-3">
          <i className="ri-error-warning-line text-primary-600 text-base mt-0.5"></i>
          <p className="text-base text-primary-800">{error}</p>
        </div>
      )}

      <div className="pt-2">
        <p className="text-[13px] text-foreground-400 mb-5 leading-relaxed">
          品牌經銷與合作方案詳情請直接聯繫洽詢。
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="whitespace-nowrap px-8 py-3 rounded-full text-[15px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer tracking-wide"
        >
          {submitting ? '送出中...' : '送出訊息'}
          <i className={`ml-1.5 text-[13px] ${submitting ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}`}></i>
        </button>
      </div>
    </form>
  );
}